import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  CardMedia,
  IconButton,
} from '@mui/material';
import { CheckCircle as CheckIcon, Help } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useProductLines } from 'services/Data/ProductLineServices';
import { useSubmission, useUpdateSubmission } from 'services/Data/SubmissionServices';
import { translateLabel } from 'services/Utilities/labelUtils';
import { HOST } from 'config/config';
import { BlobStorageTypeEnumValue } from 'enums/BlobStorageTypeEnum';
import {
  getActiveProductOfferings,
  getProductLineColor,
  getProductLineIcon,
  sortProductLinesByDisplayOrder,
} from 'services/Utilities/productOfferingsConfig';
import { createSubmissionPatchDocument } from 'services/Serializers/SubmissionPatchDocument';
import { getCertificationPageGlobalTourSteps } from '../../tourSteps/certificationPageGlobalTourSteps';
import GlobalTour from '../../tourSteps/GlobalTour';

const ProductLineSelectionStep = ({ isReadOnly = false }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const { submissionId } = useParams();
  const currentLanguage = i18n.language;

  const { data: submission } = useSubmission(submissionId);
  const { data: productLines, isLoading, error } = useProductLines();
  const updateSubmission = useUpdateSubmission(submissionId);

  const dealer = useMemo(() => submission?.dealer, [submission]);
  const activeProducts = useMemo(() => getActiveProductOfferings(dealer, productLines), [productLines, dealer]);
  const availableProductLines = useMemo(() => sortProductLinesByDisplayOrder(activeProducts), [activeProducts]);

  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourSteps, setTourSteps] = useState([]);

  useEffect(() => {
    setTourSteps(getCertificationPageGlobalTourSteps(t));
  }, [t]);

  const nextTourStep = () => {
    if (tourStep < tourSteps.length - 1) {
      setTourStep(tourStep + 1);
    } else {
      setShowTour(false);
      setTourStep(0);
    }
  };

  const closeTour = () => {
    setShowTour(false);
    setTourStep(0);
  };

  function startTour() {
    const cleaned = tourSteps.filter(step => document.querySelector(step.target));

    setTourSteps(cleaned);
    setTourStep(0);
    setShowTour(true);
  }

  const handleHeroUnitProductLineSelected = async heroUnitProductLineId => {
    if (isReadOnly) return;
    try {
      const patchDoc = createSubmissionPatchDocument({ heroUnitProductLineId });
      await updateSubmission.mutateAsync(patchDoc);
      history.push(`/dealer/certification/submissions/${submissionId}/step/2`);
    } catch (err) {
      console.error('Error selecting product line:', err);
    }
  };

  const getProductLineAvailability = productLine => {
    if (!dealer) return false;
    return (dealer.dealerProductLines ?? []).some(line => line.id === productLine.id);
  };

  const selectableProductLines = useMemo(() => availableProductLines.filter(pl => getProductLineAvailability(pl)), [
    availableProductLines,
    dealer,
  ]);
  const mdJustifyContent = selectableProductLines.length <= 3 ? 'space-evenly' : 'space-between';
  const getImageUrl = productLine => {
    if (productLine.imageFile?.id) {
      return `${HOST}/api/dashboard/attached-files/product-line/${productLine.id}/${productLine.imageFile.id}/download?type=${BlobStorageTypeEnumValue.CertificationTool}`;
    }
    return null;
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress sx={theme => ({ color: theme.palette.primary.main })} />
        <Typography sx={{ mt: 2, color: 'text.secondary' }}>{t('common.loading')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {t('submission.loadingFailed')} {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h1" sx={theme => ({ fontWeight: 600, color: theme.palette.primary.main })}>
          {t('heroUnit.selectYourHeroUnit')}
        </Typography>
        <Typography variant="body2" color="text.primary">
          {t('heroUnit.selectDescription')}
        </Typography>
      </Box>
      <IconButton
        onClick={() => startTour()}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          bgcolor: '#FFD200',
          color: '#000',
          zIndex: 1000,
          '&:hover': {
            bgcolor: '#B8941E',
          },
        }}>
        <Help />
      </IconButton>
      <Grid
        container
        data-tour="heroUnit"
        spacing={4}
        alignItems="stretch"
        sx={{
          flexWrap: { xs: 'wrap', sm: 'wrap', md: 'nowrap' },
          justifyContent: { xs: 'flex-start', md: mdJustifyContent },
        }}>
        {selectableProductLines.map(productLine => {
          const unitName = translateLabel(productLine.name, currentLanguage);
          const unitDescription = translateLabel(productLine.description, currentLanguage);
          const isSelected = submission?.heroUnitProductLine_Id === productLine.id;
          const imageUrl = getImageUrl(productLine);
          const color = getProductLineColor(productLine.internalName);
          const IconComponent = getProductLineIcon(productLine.internalName);

          return (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              key={productLine.id}
              sx={{
                display: 'flex',
                flex: { md: '1 1 0' },
                minWidth: { md: 0 },
              }}>
              <Card
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? '#1c1c1c' : theme.palette.background.paper,
                  border: isSelected
                    ? `2px solid ${color}`
                    : `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.5 : 1)}`,
                  borderRadius: 2,
                  boxShadow:
                    theme.palette.mode === 'dark'
                      ? isSelected
                        ? `0 0 20px ${color}40`
                        : '0 2px 8px rgba(0, 0, 0, 0.4)'
                      : isSelected
                      ? `0 0 0 2px ${color}40`
                      : theme.shadows[1],
                  transition: 'all 0.3s ease',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow:
                      theme.palette.mode === 'dark'
                        ? `0 8px 20px ${color}40`
                        : `0 8px 20px ${alpha(theme.palette.common.black, 0.12)}`,
                    border: `2px solid ${color}`,
                  },
                }}>
                <CardActionArea
                  disabled={isReadOnly}
                  onClick={() => handleHeroUnitProductLineSelected(productLine.id)}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    cursor: isReadOnly ? 'not-allowed' : 'pointer',
                    opacity: isReadOnly ? 0.7 : 1,
                  }}>
                  {isSelected && (
                    <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                      <CheckIcon sx={{ color, fontSize: 28 }} />
                    </Box>
                  )}

                  {imageUrl ? (
                    <CardMedia
                      component="img"
                      height="200"
                      image={imageUrl}
                      alt={unitName}
                      sx={{
                        objectFit: 'cover',
                        bgcolor: '#000',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: theme.palette.background.default, //theme.palette.mode === 'dark' ? '#0a0a0a' : '#f0f0f0',
                        color: color,
                      }}>
                      <IconComponent sx={{ fontSize: 72 }} />
                    </Box>
                  )}

                  <CardContent sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography
                      variant="h2"
                      sx={{
                        color: color,
                        fontWeight: 700,
                        mb: 1,
                      }}>
                      {unitName}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <GlobalTour show={showTour} steps={tourSteps} stepIndex={tourStep} onClose={closeTour} onNext={nextTourStep} t={t} />
    </Box>
  );
};

export default ProductLineSelectionStep;
